import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../lib/api'
import type { UserNote } from '../lib/types'

interface Props {
  note: UserNote | null
  onSave: (content: string) => Promise<void>
  companyName?: string
}

export default function NoteTaker({ note, onSave, companyName }: Props) {
  const [content, setContent] = useState(note?.content || '')
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<string | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [analysis, setAnalysis] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (note?.content !== undefined) setContent(note.content)
  }, [note?.content])

  const save = useCallback(async (text: string) => {
    setSaving(true)
    try {
      await onSave(text)
      setLastSaved(new Date().toLocaleTimeString())
    } finally {
      setSaving(false)
    }
  }, [onSave])

  const handleChange = (text: string) => {
    setContent(text)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => save(text), 1500)
  }

  const handleBlur = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (content !== (note?.content || '')) save(content)
  }

  const runAnalysis = async (type: 'summary' | 'social' | 'both') => {
    if (!content.trim()) return
    setAnalyzing(true)
    setAnalysis(null)
    try {
      const { analysis: result } = await api.aiAnalyze(content, companyName, type)
      setAnalysis(result)
    } catch {
      setAnalysis('Analysis failed. Please try again.')
    } finally {
      setAnalyzing(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Notes</h3>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            {saving && <span className="text-brand-600">Saving...</span>}
            {!saving && lastSaved && <span>Last saved {lastSaved}</span>}
          </div>
        </div>

        <textarea
          value={content}
          onChange={e => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder="Type your notes here... Auto-saves after 1.5 seconds of inactivity."
          className="input min-h-[300px] resize-y font-mono text-sm leading-relaxed"
          spellCheck={true}
          autoCorrect="on"
        />

        <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
          <p className="text-xs text-gray-400">
            {content.length} characters &middot; Auto-saves while you type
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => save(content)}
              disabled={saving}
              className="btn-secondary text-sm py-1"
            >
              Save Now
            </button>
          </div>
        </div>
      </div>

      {/* AI Analysis */}
      {content.trim().length > 20 && (
        <div className="card bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-purple-800 flex items-center gap-2">
              🤖 AI Analysis
            </h3>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => runAnalysis('summary')}
              disabled={analyzing}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
            >
              {analyzing ? 'Analyzing...' : '📋 Summarize'}
            </button>
            <button
              onClick={() => runAnalysis('social')}
              disabled={analyzing}
              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              📱 Social Posts
            </button>
            <button
              onClick={() => runAnalysis('both')}
              disabled={analyzing}
              className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              ✨ Full Analysis
            </button>
          </div>

          {analyzing && (
            <div className="mt-4 flex items-center gap-2 text-purple-600 text-sm">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600" />
              Analyzing your notes with AI...
            </div>
          )}

          {analysis && (
            <div className="mt-4">
              <div className="bg-white rounded-lg p-4 text-sm whitespace-pre-wrap border border-purple-100 leading-relaxed">
                {analysis}
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => navigator.clipboard.writeText(analysis)}
                  className="text-xs text-purple-600 hover:text-purple-800"
                >
                  📋 Copy to clipboard
                </button>
                <button
                  onClick={() => setAnalysis(null)}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
